/**
 * Catálogo de permissões.
 *
 * As permissões são **dado** — vivem em tabela e novas entram por seed, sem migration
 * (ADR-017). Este catálogo existe para dar segurança de compilação a quem as exige, e
 * para semear o banco. Nunca use uma string literal solta em `requirePermission`.
 */
export type PermissionKey = string;

export const PERMISSION = {
  PROJECTS_VIEW: 'projects.view',
  PROJECTS_MANAGE: 'projects.manage',

  STRUCTURE_VIEW: 'structure.view',
  STRUCTURE_MANAGE: 'structure.manage',

  TAKEOFF_VIEW: 'takeoff.view',
  TAKEOFF_MANAGE: 'takeoff.manage',

  SCHEDULE_VIEW: 'schedule.view',
  SCHEDULE_IMPORT: 'schedule.import',
  SCHEDULE_EDIT: 'schedule.edit',

  MATERIALS_VIEW: 'materials.view',
  MATERIALS_MANAGE: 'materials.manage',

  REQUIREMENTS_VIEW: 'requirements.view',
  REQUIREMENTS_STATUS_CHANGE: 'requirements.status.change',

  RESTRICTIONS_VIEW: 'restrictions.view',
  RESTRICTIONS_MANAGE: 'restrictions.manage',

  EMPLOYEES_VIEW: 'employees.view',
  EMPLOYEES_MANAGE: 'employees.manage',

  LABOR_PLAN: 'labor.plan',
  LABOR_ALLOCATE_OVERCAPACITY: 'labor.allocate.overcapacity',
  LABOR_CONFIRM: 'labor.confirm',
  LABOR_MODIFY: 'labor.modify',

  // A aba Não conformidades (Fase 37, RN-481): encerrar, cancelar e pôr fotos. Vem junto com a
  // tela — quem abre a aba encerra (D8).
  NON_CONFORMITIES_MANAGE: 'non-conformities.manage',

  AUDIT_VIEW: 'audit.view',
  REPORTS_VIEW: 'reports.view',

  USERS_MANAGE: 'users.manage',
  ROLES_MANAGE: 'roles.manage',
  BACKUP_MANAGE: 'backup.manage',
  SYSTEM_INTEGRITY: 'system.integrity',
  // A chave de API lê o sistema inteiro; quem a emite responde pelo sistema inteiro.
  API_KEYS_MANAGE: 'api-keys.manage',

  // Uma por tela (`pages.ts`). Não autorizam caso de uso nenhum: dizem se a tela aparece
  // no menu e se a rota abre. Quem lê e grava continua sendo a permissão de dado acima.
  PAGE_DASHBOARD: 'page.dashboard',
  PAGE_PROJECTS: 'page.projects',
  PAGE_STRUCTURE: 'page.structure',
  PAGE_TAKEOFF: 'page.takeoff',
  PAGE_SCHEDULE: 'page.schedule',
  PAGE_SUPPLIES: 'page.supplies',
  PAGE_WORKFORCE: 'page.workforce',
  PAGE_WEEKLY_PLAN: 'page.weekly-plan',
  PAGE_DAILY_PLAN: 'page.daily-plan',
  PAGE_INSPECTION: 'page.inspection',
  PAGE_QUALITY: 'page.quality',
  PAGE_NON_CONFORMITIES: 'page.non-conformities',
  PAGE_DISCIPLINES: 'page.disciplines',
  PAGE_SERVICES: 'page.services',
  PAGE_CHECKLISTS: 'page.checklists',
  PAGE_MATERIALS: 'page.materials',
  PAGE_PROFILES: 'page.profiles',
  PAGE_CALENDAR: 'page.calendar',
  PAGE_STORAGE: 'page.storage',
  PAGE_INTEGRITY: 'page.integrity',
  PAGE_AUDIT: 'page.audit',

  // Uma por aba de dentro de tela (`pages.ts`, RN-626). Como as de tela, não autorizam caso
  // de uso nenhum: dizem se a aba aparece e se o endereço dela abre. Os dados de cada aba
  // também são lidos por outras abas e telas, e por isso a recusa continua sendo da
  // permissão de dado (ADR-197).
  TAB_TAKEOFF_ASSEMBLY: 'tab.takeoff.assembly',
  TAB_TAKEOFF_ITEMS: 'tab.takeoff.items',
  TAB_TAKEOFF_BUDGET: 'tab.takeoff.budget',
  TAB_SCHEDULE_LABOR: 'tab.schedule.labor',
  TAB_SCHEDULE_MATERIALS: 'tab.schedule.materials',
  TAB_SCHEDULE_ROOMS: 'tab.schedule.rooms',
  TAB_WORKFORCE_EMPLOYEES: 'tab.workforce.employees',
  TAB_WORKFORCE_TEAMS: 'tab.workforce.teams',
  TAB_WORKFORCE_OUTSOURCED: 'tab.workforce.outsourced',
  TAB_WORKFORCE_ROLES: 'tab.workforce.roles',
  TAB_SERVICES_CATALOG: 'tab.services.catalog',
  TAB_SERVICES_AVERAGES: 'tab.services.averages',
} as const;

export type KnownPermissionKey = (typeof PERMISSION)[keyof typeof PERMISSION];

export interface PermissionDefinition {
  readonly key: KnownPermissionKey;
  readonly group: string;
  readonly description: string;
}

/** Semeado na Fase 1. A ordem define a apresentação na tela de perfis. */
export const PERMISSION_CATALOG: readonly PermissionDefinition[] = [
  { key: PERMISSION.PROJECTS_VIEW, group: 'Obras', description: 'Visualizar obras' },
  { key: PERMISSION.PROJECTS_MANAGE, group: 'Obras', description: 'Criar e editar obras' },

  {
    key: PERMISSION.STRUCTURE_VIEW,
    group: 'Estrutura',
    description: 'Visualizar a estrutura da obra',
  },
  {
    key: PERMISSION.STRUCTURE_MANAGE,
    group: 'Estrutura',
    description: 'Criar e editar torres, pavimentos, disciplinas e atividades',
  },

  // Separadas da estrutura de propósito: quem cadastra torre e prazo não é
  // necessariamente quem levanta quantidade, e o quantitativo vira pedido de compra.
  {
    key: PERMISSION.TAKEOFF_VIEW,
    group: 'Quantitativo',
    description: 'Visualizar o quantitativo da obra',
  },
  {
    key: PERMISSION.TAKEOFF_MANAGE,
    group: 'Quantitativo',
    description: 'Criar e editar pavimentos tipo e tipologias',
  },

  { key: PERMISSION.SCHEDULE_VIEW, group: 'Cronograma', description: 'Visualizar o cronograma' },
  { key: PERMISSION.SCHEDULE_IMPORT, group: 'Cronograma', description: 'Importar cronograma' },
  {
    key: PERMISSION.SCHEDULE_EDIT,
    group: 'Cronograma',
    description: 'Editar linhas do cronograma',
  },

  {
    key: PERMISSION.MATERIALS_VIEW,
    group: 'Materiais',
    description: 'Visualizar o catálogo de materiais',
  },
  {
    key: PERMISSION.MATERIALS_MANAGE,
    group: 'Materiais',
    description: 'Gerenciar o catálogo de materiais',
  },

  {
    key: PERMISSION.REQUIREMENTS_VIEW,
    group: 'Suprimentos',
    description: 'Visualizar necessidades de material',
  },
  {
    key: PERMISSION.REQUIREMENTS_STATUS_CHANGE,
    group: 'Suprimentos',
    description: 'Alterar o status de necessidades de material',
  },

  {
    key: PERMISSION.RESTRICTIONS_VIEW,
    group: 'Riscos',
    description: 'Visualizar riscos e impedimentos',
  },
  {
    key: PERMISSION.RESTRICTIONS_MANAGE,
    group: 'Riscos',
    description: 'Criar e resolver riscos e impedimentos',
  },

  {
    key: PERMISSION.EMPLOYEES_VIEW,
    group: 'Mão de obra',
    description: 'Visualizar funcionários e recursos',
  },
  {
    key: PERMISSION.EMPLOYEES_MANAGE,
    group: 'Mão de obra',
    description: 'Gerenciar funcionários, funções, equipes e serviços',
  },

  { key: PERMISSION.LABOR_PLAN, group: 'Planejamento', description: 'Planejar mão de obra' },
  {
    key: PERMISSION.LABOR_ALLOCATE_OVERCAPACITY,
    group: 'Planejamento',
    description: 'Alocar acima da capacidade, com justificativa',
  },
  {
    key: PERMISSION.LABOR_CONFIRM,
    group: 'Planejamento',
    description: 'Confirmar a conferência diária',
  },
  {
    key: PERMISSION.LABOR_MODIFY,
    group: 'Planejamento',
    description: 'Alterar o planejamento durante a conferência',
  },

  {
    key: PERMISSION.NON_CONFORMITIES_MANAGE,
    group: 'Qualidade',
    description: 'Encerrar e cancelar não conformidades, e pôr fotos nelas',
  },

  {
    key: PERMISSION.AUDIT_VIEW,
    group: 'Sistema',
    description: 'Consultar o histórico e a auditoria',
  },
  {
    key: PERMISSION.REPORTS_VIEW,
    group: 'Sistema',
    description: 'Visualizar indicadores e relatórios',
  },
  { key: PERMISSION.USERS_MANAGE, group: 'Sistema', description: 'Gerenciar perfis de usuário' },
  { key: PERMISSION.ROLES_MANAGE, group: 'Sistema', description: 'Gerenciar papéis e permissões' },
  // A chave continua `backup.manage` (está no banco e nos papéis); o que ela abre, desde a
  // Fase 32, é só a página de armazenamento do navegador. A semente regrava a descrição.
  {
    key: PERMISSION.BACKUP_MANAGE,
    group: 'Sistema',
    description: 'Ver o armazenamento deste navegador',
  },
  {
    key: PERMISSION.SYSTEM_INTEGRITY,
    group: 'Sistema',
    description: 'Verificar e corrigir a integridade dos dados',
  },
  {
    key: PERMISSION.API_KEYS_MANAGE,
    group: 'Sistema',
    description: 'Criar e revogar chaves de API (leitura do sistema inteiro por agentes)',
  },

  { key: PERMISSION.PAGE_DASHBOARD, group: 'Telas', description: 'Abrir a tela Painel' },
  { key: PERMISSION.PAGE_PROJECTS, group: 'Telas', description: 'Abrir a tela Obras' },
  { key: PERMISSION.PAGE_STRUCTURE, group: 'Telas', description: 'Abrir a tela Estrutura' },
  { key: PERMISSION.PAGE_TAKEOFF, group: 'Telas', description: 'Abrir a tela Quantitativo' },
  { key: PERMISSION.PAGE_SCHEDULE, group: 'Telas', description: 'Abrir a tela Cronograma' },
  { key: PERMISSION.PAGE_SUPPLIES, group: 'Telas', description: 'Abrir a tela Suprimentos' },
  { key: PERMISSION.PAGE_WORKFORCE, group: 'Telas', description: 'Abrir a tela Mão de obra' },
  { key: PERMISSION.PAGE_WEEKLY_PLAN, group: 'Telas', description: 'Abrir a tela Planejamento' },
  {
    key: PERMISSION.PAGE_DAILY_PLAN,
    group: 'Telas',
    description: 'Abrir a tela Programação do dia',
  },
  { key: PERMISSION.PAGE_INSPECTION, group: 'Telas', description: 'Abrir a tela Conferência' },
  {
    key: PERMISSION.PAGE_QUALITY,
    group: 'Telas',
    description: 'Abrir a tela Qualidade por serviço',
  },
  {
    key: PERMISSION.PAGE_NON_CONFORMITIES,
    group: 'Telas',
    description: 'Abrir a tela Não conformidades',
  },
  { key: PERMISSION.PAGE_DISCIPLINES, group: 'Telas', description: 'Abrir a tela Disciplinas' },
  { key: PERMISSION.PAGE_SERVICES, group: 'Telas', description: 'Abrir a tela Serviços' },
  { key: PERMISSION.PAGE_CHECKLISTS, group: 'Telas', description: 'Abrir a tela Checklists' },
  { key: PERMISSION.PAGE_MATERIALS, group: 'Telas', description: 'Abrir a tela Materiais' },
  { key: PERMISSION.PAGE_PROFILES, group: 'Telas', description: 'Abrir a tela Perfis' },
  { key: PERMISSION.PAGE_CALENDAR, group: 'Telas', description: 'Abrir a tela Calendário' },
  { key: PERMISSION.PAGE_STORAGE, group: 'Telas', description: 'Abrir a tela Armazenamento' },
  { key: PERMISSION.PAGE_INTEGRITY, group: 'Telas', description: 'Abrir a tela Integridade' },
  { key: PERMISSION.PAGE_AUDIT, group: 'Telas', description: 'Abrir a tela Auditoria' },

  {
    key: PERMISSION.TAB_TAKEOFF_ASSEMBLY,
    group: 'Abas',
    description: 'Ver a aba Montagem do Quantitativo',
  },
  {
    key: PERMISSION.TAB_TAKEOFF_ITEMS,
    group: 'Abas',
    description: 'Ver a aba Itens e consolidação do Quantitativo',
  },
  {
    key: PERMISSION.TAB_TAKEOFF_BUDGET,
    group: 'Abas',
    description: 'Ver a aba Orçamento do Quantitativo',
  },
  {
    key: PERMISSION.TAB_SCHEDULE_LABOR,
    group: 'Abas',
    description: 'Ver a aba Mão de obra do Cronograma',
  },
  {
    key: PERMISSION.TAB_SCHEDULE_MATERIALS,
    group: 'Abas',
    description: 'Ver a aba Materiais do Cronograma',
  },
  {
    key: PERMISSION.TAB_SCHEDULE_ROOMS,
    group: 'Abas',
    description: 'Ver a aba Execução por cômodo do Cronograma',
  },
  {
    key: PERMISSION.TAB_WORKFORCE_EMPLOYEES,
    group: 'Abas',
    description: 'Ver a aba Funcionários da Mão de obra',
  },
  {
    key: PERMISSION.TAB_WORKFORCE_TEAMS,
    group: 'Abas',
    description: 'Ver a aba Equipes da Mão de obra',
  },
  {
    key: PERMISSION.TAB_WORKFORCE_OUTSOURCED,
    group: 'Abas',
    description: 'Ver a aba Serviços terceirizados da Mão de obra',
  },
  {
    key: PERMISSION.TAB_WORKFORCE_ROLES,
    group: 'Abas',
    description: 'Ver a aba Funções e disciplinas da Mão de obra',
  },
  {
    key: PERMISSION.TAB_SERVICES_CATALOG,
    group: 'Abas',
    description: 'Ver a aba Catálogo dos Serviços',
  },
  {
    key: PERMISSION.TAB_SERVICES_AVERAGES,
    group: 'Abas',
    description: 'Ver a aba Taxas médias dos Serviços',
  },
];
